import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, Clock, TrendingUp, AlertCircle, LogOut, Plus, Loader2, Wallet, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import PushNotifications from '@/components/PushNotifications';
import QuickNav from '@/components/QuickNav';

import { API } from '@/lib/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
    fetchSubscription();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/analysis/history`, {
        withCredentials: true
      });
      setAnalyses(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        withCredentials: true
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getRiskConfig = (level) => {
    switch (level) {
      case 'alto':
        return { icon: '🔴', color: 'text-red-500', bg: 'bg-red-500/10' };
      case 'medio':
        return { icon: '🟡', color: 'text-yellow-400', bg: 'bg-yellow-400/10' };
      case 'baixo':
        return { icon: '🟢', color: 'text-[#00FF94]', bg: 'bg-[#00FF94]/10' };
      default:
        return { icon: '🟡', color: 'text-yellow-400', bg: 'bg-yellow-400/10' };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white ml-12 sm:ml-14"
              onClick={() => navigate('/')}
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Início
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
              <span className="font-bold text-white">Evite Dívidas</span>
            </div>
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <main className="pt-24 pb-16" data-testid="dashboard-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome Section */}
          <motion.div
            className="mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                  Olá, {user?.name?.split(' ')[0] || 'Usuário'}! 👋
                </h1>
                <p className="text-gray-400">
                  Veja o histórico das suas análises financeiras
                </p>
              </div>
              <PushNotifications user={user} />
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00FF94]/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#00FF94]" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white font-mono">{analyses.length}</p>
                  <p className="text-sm text-gray-500">Análises realizadas</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white font-mono">
                    {analyses.filter(a => a.risk_level === 'alto').length}
                  </p>
                  <p className="text-sm text-gray-500">Alto risco identificado</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00FF94]/10 flex items-center justify-center">
                  <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white font-mono">
                    {analyses.filter(a => a.risk_level === 'baixo').length}
                  </p>
                  <p className="text-sm text-gray-500">Baixo risco</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* New Analysis Button */}
          <motion.div
            className="mb-8 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button 
              size="lg"
              className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold px-8 py-6 glow-primary"
              onClick={() => navigate('/analyze')}
              data-testid="new-analysis-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nova Análise
            </Button>
            
            <Button 
              size="lg"
              variant="outline"
              className={`rounded-full font-bold px-8 py-6 ${
                subscription?.subscription_type === 'premium' 
                  ? 'border-[#00FF94]/50 text-[#00FF94] hover:bg-[#00FF94]/10' 
                  : 'border-white/10 text-white hover:bg-white/5'
              }`}
              onClick={() => navigate('/expenses')}
              data-testid="expenses-btn"
            >
              <Wallet className="w-5 h-5 mr-2" />
              Organizador de Despesas
              {subscription?.subscription_type !== 'premium' && (
                <Crown className="w-4 h-4 ml-2 text-yellow-500" />
              )}
            </Button>
          </motion.div>

          {/* History Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              Histórico de Análises
            </h2>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
              </div>
            ) : analyses.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-12 text-center">
                <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Nenhuma análise ainda</h3>
                <p className="text-gray-500 mb-6">
                  Faça sua primeira análise para proteger suas finanças
                </p>
                <Button 
                  className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full"
                  onClick={() => navigate('/analyze')}
                >
                  Fazer primeira análise
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {analyses.map((analysis, index) => {
                  const riskConfig = getRiskConfig(analysis.risk_level);
                  return (
                    <motion.div
                      key={analysis.analysis_id}
                      className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6 cursor-pointer card-hover"
                      onClick={() => navigate(`/result/${analysis.analysis_id}`)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      data-testid={`analysis-item-${index}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl">{riskConfig.icon}</span>
                            <span className={`text-sm font-medium ${riskConfig.color} ${riskConfig.bg} px-3 py-1 rounded-full`}>
                              {analysis.risk_level === 'alto' ? 'Alto Risco' : 
                               analysis.risk_level === 'medio' ? 'Risco Medio' : 'Baixo Risco'}
                            </span>
                          </div>
                          <h3 className="text-white font-medium mb-2 line-clamp-2">
                            {analysis.decision_description}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>R$ {analysis.total_value.toLocaleString('pt-BR')}</span>
                            <span>•</span>
                            <span>{formatDate(analysis.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </main>
      <QuickNav />
    </div>
  );
};

export default Dashboard;
